import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import { projectsAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Video,
  Settings,
  Users,
  LogOut,
  User,
  Shield,
  FolderOpen,
  Hexagon,
  Plus
} from 'lucide-react';

export default function Layout() {
  const { user, logout } = useAuth();
  const { selectedProject, selectProject } = useProject();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [projectForm, setProjectForm] = useState({ name: '', description: '' });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const result = await projectsAPI.getAll();
      setProjects(result.projects || []);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleProjectChange = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    selectProject(project);
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const result = await projectsAPI.create(projectForm);
      setShowProjectDialog(false);
      setProjectForm({ name: '', description: '' });
      await loadProjects();
      // Auto-select the newly created project
      if (result.project) {
        selectProject(result.project);
      }
    } catch (err) {
      console.error('Failed to create project:', err);
    }
  };

  const isAdminPage = location.pathname.startsWith('/admin');

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Compact Top Nav */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 py-2">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <Hexagon className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg">PolygonZone</span>
          </div>

          {/* Center: Project Filter + Nav */}
          <div className="flex items-center gap-4">
            {/* Project Selector */}
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              <Select
                value={selectedProject?.id || ''}
                onValueChange={handleProjectChange}
              >
                <SelectTrigger className="w-[200px] h-8">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                onClick={() => setShowProjectDialog(true)}
                title="Create New Project"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <Separator orientation="vertical" className="h-6" />

            {/* Nav Links */}
            <nav className="flex items-center gap-1">
              <NavLink to="/projects">
                {({ isActive }) => (
                  <Button
                    variant={isActive ? 'default' : 'ghost'}
                    size="sm"
                    className="gap-2"
                  >
                    <FolderOpen className="h-4 w-4" />
                    Projects
                  </Button>
                )}
              </NavLink>

              <NavLink to="/cameras">
                {({ isActive }) => (
                  <Button
                    variant={isActive ? 'default' : 'ghost'}
                    size="sm"
                    className="gap-2"
                  >
                    <Video className="h-4 w-4" />
                    Cameras
                  </Button>
                )}
              </NavLink>

              {user?.role === 'admin' && (
                <>
                  <NavLink to="/admin">
                    {({ isActive }) => (
                      <Button
                        variant={isActive || isAdminPage ? 'default' : 'ghost'}
                        size="sm"
                        className="gap-2"
                      >
                        <Settings className="h-4 w-4" />
                        Admin
                      </Button>
                    )}
                  </NavLink>
                  <NavLink to="/admin/users">
                    {({ isActive }) => (
                      <Button
                        variant={isActive ? 'default' : 'ghost'}
                        size="sm"
                        className="gap-2"
                      >
                        <Users className="h-4 w-4" />
                        Users
                      </Button>
                    )}
                  </NavLink>
                </>
              )}
            </nav>
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <div className="flex flex-col">
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium">{user?.name || 'User'}</span>
                  {user?.role === 'admin' && (
                    <Badge variant="secondary" className="h-4 px-1 text-xs">
                      <Shield className="h-3 w-3" />
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="gap-2"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Create Project Dialog */}
      <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Add a new project to organize your cameras and zones
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateProject}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="project-name">Project Name *</Label>
                <Input
                  id="project-name"
                  value={projectForm.name}
                  onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                  placeholder="My Project"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-description">Description</Label>
                <Input
                  id="project-description"
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                  placeholder="Project description (optional)"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowProjectDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Project</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
